import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import ButtonPrimary from '../../buttons/primary/primary';
import SecondaryButton from '../../buttons/secondary/secondary';
import Loader from '../../loader/loader';
import { useAlert } from '../../alert/alert';
import { SEVERITY } from '../../../shared/interfaces/alert';
import { fetchRubricasGrouped } from '../../../features/rubrica/service/rubrica';
import { ProjectRubrica } from '../../../shared/interfaces/rubrica';
import './style.css';
import RubricaField from './rubrica-field';
import CreateRubricaModal, { CreateRubricaPayload, RubricaTypeOption } from './create-rubrica-modal';

interface Rubrica {
  id: string;
  name: string;
  description: string;
  typeId?: string;
  value: string;
  label: string;
  group?: string; // Adicionar grupo para o SelectGroup
}

export interface PendingRubricaOption {
  tempId: string;
  name: string;
  description: string;
  typeId?: string | null;
  typeName?: string;
}

interface RubricaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rubricas: ProjectRubrica[]) => void;
  initialRubricas?: ProjectRubrica[];
  isLoading?: boolean;
  rubricaTypes?: RubricaTypeOption[];
  pendingRubricas?: PendingRubricaOption[];
  onCreatePendingRubrica?: (payload: CreateRubricaPayload) => PendingRubricaOption;
}

interface FormData {
  rubricas: ProjectRubrica[];
}

export default function RubricaModal({
  isOpen,
  onClose,
  onSave,
  initialRubricas = [],
  isLoading = false,
  rubricaTypes = [],
  pendingRubricas = [],
  onCreatePendingRubrica,
}: RubricaModalProps) {
  const { showAlert } = useAlert();
  const [fetchedRubricas, setFetchedRubricas] = useState<Rubrica[]>([]);
  const [isLoadingRubricas, setIsLoadingRubricas] = useState(false);
  const [createState, setCreateState] = useState<{
    open: boolean;
    initialName: string;
    onCreated?: (option: { tempId: string; name: string }) => void;
  }>({ open: false, initialName: '' });

  const requestCreateRubrica = useCallback(
    (name: string, onCreated: (option: { tempId: string; name: string }) => void) => {
      if (!onCreatePendingRubrica) return;
      setCreateState({ open: true, initialName: name, onCreated });
    },
    [onCreatePendingRubrica]
  );

  const closeCreateModal = useCallback(() => {
    setCreateState({ open: false, initialName: '' });
  }, []);

  const availableRubricas = useMemo<Rubrica[]>(() => {
    const pendingAsOptions: Rubrica[] = pendingRubricas.map((pending) => ({
      id: pending.tempId,
      name: pending.name,
      description: pending.description,
      value: pending.tempId,
      label: pending.name,
      group: pending.typeName || 'Novas',
    }));

    // Garante que rubricas já vinculadas ao projeto continuem renderizáveis no select
    // mesmo se o backend filtra por empresa e oculta alguma delas (ex.: rubricas
    // criadas por outra empresa antes da regra de clientId entrar em vigor).
    const fetchedIds = new Set(fetchedRubricas.map((r) => r.id));
    const pendingIds = new Set(pendingRubricas.map((p) => p.tempId));
    const initialAsOptions: Rubrica[] = initialRubricas
      .filter((ir) => ir.rubricaId && !fetchedIds.has(ir.rubricaId) && !pendingIds.has(ir.rubricaId))
      .map((ir) => ({
        id: ir.rubricaId,
        name: ir.rubrica?.name || ir.name || '',
        description: ir.rubrica?.description || ir.description || '',
        value: ir.rubricaId,
        label: ir.rubrica?.name || ir.name || '',
        group: 'Outros',
      }));

    return [...fetchedRubricas, ...initialAsOptions, ...pendingAsOptions];
  }, [fetchedRubricas, pendingRubricas, initialRubricas]);

  const formMethods = useForm<FormData>({
    defaultValues: {
      rubricas: initialRubricas.length > 0 ? initialRubricas : [{ rubricaId: '', valor: 0 }]
    },
    mode: 'onChange'
  });

  const { control, handleSubmit, reset, watch, formState: { errors, isValid } } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rubricas'
  });

  const watchedRubricas = watch('rubricas') || [];

  const fetchRubricasData = useCallback(async () => {
    setIsLoadingRubricas(true);
    try {
      const data = await fetchRubricasGrouped();
      
      // Os dados já vêm formatados corretamente da API: {label, value, group}
      // Só precisamos mapear para o formato interno
      if (Array.isArray(data)) {
        const formattedRubricas = data.map((rubrica: any) => ({
          id: rubrica.value, // O ID único está no campo 'value'
          name: rubrica.label, // O nome está no campo 'label'
          description: rubrica.label, // Usar o label como descrição também
          value: rubrica.value, // Manter o value
          label: rubrica.label, // Manter o label
          group: rubrica.group // Manter o grupo para agrupamento
        }));
        
        setFetchedRubricas(formattedRubricas);
      } else {
        throw new Error('Dados da API não estão no formato esperado');
      }
    } catch (error) {
      showAlert('Erro ao carregar rubricas', SEVERITY.ERROR);

      setFetchedRubricas([]);
    } finally {
      setIsLoadingRubricas(false);
    }
  }, [showAlert]);

  const addRubrica = () => {
    append({ rubricaId: '', valor: 0 });
  };

  const removeRubrica = (index: number) => {
    remove(index);
  };

  const getTotalValue = () => {
    return watchedRubricas.reduce((total, rubrica) => total + Number(rubrica.valor || 0), 0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getUsedRubricaIds = useCallback(() => {
    return watchedRubricas.map(r => r.rubricaId).filter(id => id);
  }, [watchedRubricas]);

  const getAvailableRubricasForField = (currentRubricaId: string) => {
    const usedIds = getUsedRubricaIds();
    return availableRubricas
      .filter(r => r.id === currentRubricaId || !usedIds.includes(r.id));
  };

  const onSubmit = (data: FormData) => {
    if (!data.rubricas || data.rubricas.length === 0) {
      onSave([]);
      onClose();
      return;
    }

    const validRubricas = data.rubricas
      .filter(r => r.rubricaId && r.valor > 0)
      .map(rubrica => {
        const fullRubrica = availableRubricas.find(r => r.id === rubrica.rubricaId);

        return {
          ...rubrica,
          name: fullRubrica?.name || fullRubrica?.label || '',
          description: fullRubrica?.description || fullRubrica?.label || '',
          rubrica: {
            id: rubrica.rubricaId,
            name: fullRubrica?.name || fullRubrica?.label || '',
            description: fullRubrica?.description || fullRubrica?.label || ''
          }
        };
      });

    if (validRubricas.length === 0) {
      showAlert('Adicione pelo menos uma rubrica com valor maior que zero', SEVERITY.WARNING);
      return;
    }

    onSave(validRubricas);
    onClose();
  };

  const handleSaveRubricas = (event?: React.BaseSyntheticEvent) => {
    event?.preventDefault();

    if (fields.length === 0) {
      onSave([]);
      onClose();
      return;
    }

    void handleSubmit(onSubmit)(event);
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  // Só reseta quando o modal transita de fechado → aberto.
  // Antes dependíamos de `initialRubricas`/`reset`/`fetchRubricasData`, mas o parent
  // recria `initialRubricas = [...projectRubricas]` em todo render — qualquer re-render
  // disparava `reset()` e o `useFieldArray` recriava os slots, descartando rubricas
  // pendentes recém-adicionadas.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      fetchRubricasData();
      reset({
        rubricas: initialRubricas.length > 0 ? initialRubricas : [{ rubricaId: '', valor: 0 }]
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialRubricas, reset, fetchRubricasData]);

  if (!isOpen) return null;

  const hasAvailableRubricas = availableRubricas.length > getUsedRubricaIds().length;

  return (
    <FormProvider {...formMethods}>
      <div className="rubrica-modal-overlay">
        <div className="rubrica-modal">
          {(isLoading || isLoadingRubricas) && (
            <div className="rubrica-modal-loader">
              <Loader />
            </div>
          )}
          
          <div className="rubrica-modal__header">
            <h2 className="rubrica-modal__title">Gerenciar Rubricas do Projeto</h2>
            <button 
              className="rubrica-modal__close" 
              onClick={handleCancel}
              type="button"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSaveRubricas} className="rubrica-modal__form">
            <div className="rubrica-modal__content">
              <div className="rubrica-modal__info">
                <p className="rubrica-modal__description">
                  Selecione as rubricas que fazem parte deste projeto e defina o valor para cada uma.
                </p>
              </div>

              <div className="rubrica-modal__rubricas-scroll">
                <div className="rubrica-modal__fields">
                  {fields.map((field, index) => (
                    <RubricaField
                      key={field.id}
                      fieldId={field.id}
                      index={index}
                      availableRubricas={availableRubricas}
                      onRemove={() => removeRubrica(index)}
                      isLoading={isLoading}
                      isLoadingRubricas={isLoadingRubricas}
                      canRemove
                      getUsedRubricaIds={getUsedRubricaIds}
                      onCreateRubrica={onCreatePendingRubrica ? requestCreateRubrica : undefined}
                    />
                  ))}
                </div>

                <div className="rubrica-modal__add">
                  <button
                    type="button"
                    onClick={addRubrica}
                    className="rubrica-modal__add-button"
                    disabled={isLoading || isLoadingRubricas || !hasAvailableRubricas}
                  >
                    + Adicionar Rubrica
                  </button>
                </div>
              </div>

              {watchedRubricas.length > 0 && getTotalValue() > 0 && (
                <div className="rubrica-modal__summary">
                  <div className="rubrica-summary">
                    <div className="rubrica-summary__row">
                      <span className="rubrica-summary__label">Total de Rubricas:</span>
                      <span className="rubrica-summary__value">
                        {watchedRubricas.filter(r => r.rubricaId && r.valor > 0).length}
                      </span>
                    </div>
                    <div className="rubrica-summary__row rubrica-summary__total">
                      <span className="rubrica-summary__label">Valor Total do Projeto:</span>
                      <span className="rubrica-summary__value">
                        {formatCurrency(getTotalValue())}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rubrica-modal__actions">
              <SecondaryButton
                title="Cancelar"
                onClick={handleCancel}
                disabled={isLoading}
              />
              <ButtonPrimary
                title="Salvar Rubricas"
                disabled={(fields.length > 0 && !isValid) || isLoading || isLoadingRubricas}
                type="submit"
              />
            </div>
          </form>
        </div>
      </div>

      <CreateRubricaModal
        isOpen={createState.open}
        initialName={createState.initialName}
        onClose={closeCreateModal}
        onConfirm={(payload) => {
          if (!onCreatePendingRubrica) return;
          const created = onCreatePendingRubrica(payload);
          const callback = createState.onCreated;
          closeCreateModal();
          showAlert(`Rubrica "${created.name}" será criada ao salvar o projeto`, SEVERITY.INFO);
          // Aguarda o próximo tick para que `pendingRubricas` (no parent) já tenha
          // propagado a nova opção até `availableRubricas` antes de selecioná-la.
          setTimeout(() => {
            callback?.({ tempId: created.tempId, name: created.name });
          }, 0);
        }}
        types={rubricaTypes}
        isLoading={isLoading}
      />
    </FormProvider>
  );
}
